/**
 * @name 请求规则
 * @description
 * 1、具备对传参的规则校验，数据类型校验格式等。
 * 2、用于切换dev prod环境的能力，通过vuex的state控制开关, 根据baseUrl比对字典表，返回对应模式的请求baseUrl
 * 3、返回数据的校验规则，200情况下的公用300+词条的错误返回
 * 4、数据访问的url参数追加
 * 5、同类请求通用的规则
 */
import axios from 'axios';
// import CEUtils from '@cekit/ce-utils';
// import router from '@/router';
import store from 'store';
import { findKey, merge } from 'lodash';
import { Message } from 'element-ui';
import Settings from 'settings';
import Schema from 'async-validator';
import { v4 as uuidv4 } from 'uuid';
import { getAccessInstance, get } from '@/utils/access_token';
// import { getRequest } from 'utils/tools';
import { addPending, removePending, REPEATSYMBOL } from './axios-cancel-token';
import RequestManager from '../../utils/axios_request_manager';

import { logJson } from '@/utils/ce-track';
import { regOmoConfig, getOmoParams } from '@/utils/omo_config';
class BasicRequest {
    constructor() {
        this.queue = {};
        this.manager = new RequestManager();
    }

    /**
     * baseURL判定，如果输入与serverMap注册url对比成功，自动切换当前运行环境的baseURL
     * @param {string} url
     */
    getBaseUrl(url) {
        // url与生产环境的url比较，判断key
        const _key = findKey(Settings.production, url);
        if (!_key) {
            return '';
        }
        // 在present现在运行的环境，根据key拿到value
        return Settings.present[_key];
    }

    async setConfig(config) {
        if (!config.method) {
            return Promise.reject(new Error('config.method is not null!'));
        }
        if (!config.url) {
            return Promise.reject(new Error('config.url is not null!'));
        }
        /**
         * @description
         * 将 {host}/xxx/yyy 的url替换为对应服务的前缀
         * config.url = config.url.replace(/^\{(\w+)\}/, (m, $1) => present[$1] || '');
         * 判断url是否包含{},例如:{node_api}/api/get_xxx
         * 包含则不使用baseURL
         */
        const urlConf = {};
        if (config.baseURL) {
            // `baseURL` 将自动加在 `url` 前面，除非 `url` 是一个绝对 URL。
            // 它可以通过设置一个 `baseURL` 便于为 axios 实例的方法传递相对 URL
            // urlConf.baseURL = config.baseURL || baseURL;
            // baseUrl为正式地址，根据对照表找到当前环境的地址动态映射。
            const _baseURL = this.getBaseUrl(config.baseURL);
            if (!_baseURL) {
                console.warn('配置环境baseURL不存在配置表中', config.baseURL);
            }
            urlConf.baseURL = _baseURL || config.baseURL;
            urlConf.url = config.url;
        } else if (/^\{#(\w+)\}/.test(config.url)) {
            const _baseUrl = config.url.substr(0, config.url.indexOf('/'));
            const _url = config.url.substr(config.url.indexOf('/'));
            urlConf.baseURL = _baseUrl.replace(/^\{#(\w+)\}/, (m, $1) => Settings.present[$1] || '');
            urlConf.url = _url;
        } else {
            // present.base为空则使用当前的地址，baseURL()->window.location.origin
            urlConf.baseURL = Settings.present.base || Settings.baseURL();
            urlConf.url = config.url;
        }
        const cancelToken = this.manager.getNextToken('saas');

        const _config = {
            ...urlConf,
            timeout: config.timeout || 10000,
            method: config.method || 'GET',
            headers: {
                'content-type': Settings.contentType[config.dataType || 'json'],
                'X-Requested-With': 'XMLHttpRequest', // getSaasTenantInfo 需要校验用户的登录状态
                traceId: uuidv4(),
                // 'X-Token': getToken(),
            },
            cancelToken,
            // transformResponse` 在传递给 then/catch 前，允许修改响应数据
            // 根据系统类型判定返回格式的二次转换规范
            // transformResponse: [function (data) {
            //     return data;
            // }]
        };
        // if (config.logObj) {
        //     _config.headers['optlog'] = encodeURIComponent(JSON.stringify(config.logObj.optlog));
        // }
        if (config.logFlag) {
            _config.headers['optlog'] = logJson(config);
        }
        return _config;
    }

    propValidator(rules, data) {
        const _vrules = new Promise(function (resolve, reject) {
            // 参考: https://github.com/yiminghe/async-validator
            const _v = new Schema(rules);
            _v.validate(data, (errors, fields) => {
                if (errors) {
                    reject(errors);
                } else {
                    resolve(fields);
                }
            });
        });
        return _vrules;
    }

    /**
     * 《新门户应用开发规范2020-2-2修正版.docx》->4.2.3 返回格式规范
     * 业务遵循http状态码，业务参数异常码包装在data里。
     * 业务参数异常时status为200,
     * 业务参数异常码封装在data里，
     * 异常信息放入stack，
     * msg给开发者看。
     * stack信息只有在调试模式下可见，
     * 参见网关规范（debug模式调试说明）。
     * result = {
     *     status: 101,
     *     msg: 'success',
     *     data: {},
     *     stack: '异常堆栈信息',
     * };
     */
    responseDataRule(data, type) {
        const _rules = [
            {
                status: {
                    type: 'integer',
                    required: true,
                    asyncValidator: (rule, value) => {
                        return new Promise((resolve, reject) => {
                            if (value !== 999 && (value > 600 || value < 100)) {
                                reject(new TypeError('异常码超过规范值的范围，允许范围:1XX/2XX/3XX/4XX/5XX!'));
                            } else {
                                resolve();
                            }
                        });
                    },
                },
                /* msg: {
                    type: 'enum',
                    enum: ['success', 'fail', 'ok'],
                    required: true,
                }, */
                /* data: {
                     /type: 'object', // is not Array.isArray
                     required: true,
                 }, */
                stack: {
                    type: 'string', // 可以没有
                },
            },
            {
                code: {
                    type: 'integer',
                    required: false,
                    asyncValidator: (rule, value) => {
                        return new Promise((resolve, reject) => {
                            if (value > 400 && value < 300) {
                                reject(new TypeError('异常码超过规范值的范围，允许范围:3XX!'));
                            } else {
                                resolve();
                            }
                        });
                    },
                },
            },
        ];
        const _vrules = new Promise(function (resolve, reject) {
            // 参考: https://github.com/yiminghe/async-validator
            const _v = new Schema(_rules[type || 0]);
            _v.validate(data, (errors, fields) => {
                if (errors) {
                    Message({
                        message: data.msg,
                        type: 'error',
                        duration: Settings.messageDuration,
                    });
                    reject(fields);
                } else {
                    resolve();
                }
            });
        });
        return _vrules;
    }

    interceptors(instance, options) {
        // 统计接口/时长/路由
        let startTime = ''
        let endTime = ''
        let action = ''
        // end
        instance.interceptors.request.use(
            async config => {
                removePending(config, () => addPending(config));

                // 参数规则校验
                if (config.prop) {
                    // config.data:只适用于这些请求方法 'PUT', 'POST', 和 'PATCH',浏览器专属：FormData, File, Blob
                    // config.params: 是即将与请求一起发送的 URL 参数必须是一个无格式对象(plain object)或 URLSearchParams 对象
                    await this.propValidator(config.prop, config.method !== 'get' ? config.data : config.params);
                }
                /**
                 * @warning 因为后台开发的请求参数违背了post和get规范，post请求的path要追加属性
                 */
                if (Settings.paramsPath) {
                    if (!config.params) {
                        config.params = {};
                    }
                    // 因兼容2324导致的instance不匹配问题解决方案
                    if (store.state.$is2024) {
                        config.params['instance'] = (config.params && config.params.instance) || getAccessInstance();
                    } else {
                        config.params['instance'] = getAccessInstance();
                    }
                    config.params['tenantId'] = (config.params && config.params.tenantId) || get(Settings.userDataKey) && get(Settings.userDataKey).tenantId;                    
                    if (sessionStorage.getItem('TOKEN')) {
                        config.params['TOKEN'] = sessionStorage.getItem('TOKEN');
                    }

                    // 针对omo处理
                    if (regOmoConfig()) {
                        delete config.params['TOKEN']; // 删除token
                        config.params['instance'] = getOmoParams('instance');
                        config.params['tkt'] = getOmoParams('tkt');
                        config.params['invokeFrom'] = getOmoParams('invokeFrom') || 'cms';
                        // return
                    }

                    // 获得路径中的公用参数, 并将对象传递给get参数中
                    const pathValues = get('publicPathValues');
                    if (pathValues && Object.keys(pathValues).length > 0) {
                        //Object.assign(pathValues,config.params);
                        Object.assign(config.params, pathValues);
                    }
                    if (config.data) {
                        Settings.paramsPath.map(_param => {
                            if (!(config.keepParamsInBody && config.keepParamsInBody.includes(_param))) {
                                delete config.data[_param];
                            }
                        });
                    }
                }
                // 添加全局的loading...
                // if (!Object.keys(this.queue).length) {
                // Spin.show() 如果存在请求则等待 loading...
                // }
                this.queue[options.url] = true;
                // 发送请求之前做什么？
                if (store.getters.token) {
                    // 让每个请求携带令牌
                    // ['X-Token'] 是自定义 headers key
                    // 请根据请求实际修改
                    // config.headers['X-Token'] = getToken();
                }


//                 console.log('当前页面请求接口数：', store.state.requestCont.num++);
//                 store.commit('requestCont/SET_NUM', store.state.requestCont.num++);
//                 let xx = '';
//                 if(config.url.indexOf('/npgw') > -1) {
//                     xx = config.url.substring(config.url.indexOf('/npgw'))
//                 }
//                 if(config.url.indexOf('/bm') > -1) {
//                     xx = config.url.substring(config.url.indexOf('/bm'))
//                 }
//                 store.commit('requestCont/SET_TABLEURL', store.state.requestCont.reqUrlArr.push(xx));
//                 //if(store.state.requestCont.num == store.state.requestCont.reqUrlArr.length) {
//                     console.table(store.state.requestCont.reqUrlArr);
// //                }

                // 统计接口/时长/路由
                startTime = new Date().getTime()
                action = config.url.substring(config.url.indexOf('.com'), config.url.length)
                // end

                return config;
            },
            error => {
                // 处理错误请求
                return Promise.reject(error);
            },
        );
        instance.interceptors.response.use(
            /**
             * 如果你想获取 headers or status
             * 请返回  response => response
             * 通过自定义代码确定请求状态
             * 请参考用例
             * 可以通过HTTP状态码来判断状态
             */
            async response => {
                console.log(response);
                removePending(response.config);
                const { data, status } = response;
                console.warn('response:', response);
                /**
                 * 《新门户应用开发规范2020-2-2修正版.docx》->4.2.3 返回格式规范
                 */
                if (data) {
                    await this.responseDataRule(data);
                } else {
                    return Promise.reject(new Error('请求没有返回数据'));
                }
                // 如果不等于(默认:200或0)，则判断为网关错误根据词条显示
                // status不在正确的网关successStatus代码中
                if (!Settings.successStatus.includes(status)) {
                    /**
                     * @description 兼容国际化词条问题
                     * @todo 国际化问题
                     */
                    Message({
                        message: data.stack || 'Error',
                        type: 'error',
                        duration: Settings.messageDuration,
                    });

                    return Promise.reject(new Error('请求网关错误, 非正常网关状态，或数据类型错误!'));
                }
                if (Settings.successCode.includes(status)) {
                    console.log('request_product');
                    // 登录失效code, 默认999
                    if (Settings.invalidCode === data.status) {
                        // debugger;
                        // // 999 分为2种 无cookie 与 cookie失效 第一种直接跳转B端登录 第二种应该传给后端backUrl 实现免登 判断依据为是否存在实例号与token
                        // const instance = getAccessInstance();
                        // const token = getAccessToken();
                        // console.log(getAccessInstance(), getAccessToken(), '获取token或实例');
                        // if (instance && token) {
                        //     let locations = decodeURIComponent(data.location);
                        //     let connectSymbol = locations.indexOf('?') !== -1 ? '&backurl=' : '?backurl=';
                        //     let href = window.location.href
                        //     let uri = data.location + encodeURIComponent(connectSymbol + encodeURIComponent(href));
                        //     // window.location.href = uri;
                        //     console.log(uri, '有instance且有token跳转路径，带backurl');
                        // } else {
                        //     // window.location.href = `//${instance}.${.$bmSuffix}/m-login`;
                        //     // window.location.href = data.location;
                        //     console.log(data.location, '无cookie,直接跳转不处理');
                        // }
                        let locations = decodeURIComponent(data.location);
                        let connectSymbol = locations.indexOf('?') !== -1 ? '&backurl=' : '?backurl=';
                        let href = window.location.href;
                        let uri = data.location + encodeURIComponent(connectSymbol + encodeURIComponent(href));

                        console.log(uri, '有instance且有token跳转路径，带backurl');
                        window.location.href = uri;
                    }
                    // if (Settings.invalidCode === data.status) {
                    //     // 如果出现999 则不能访问网络的其它接口
                    //     // set('network', false);
                    //     // 登录失效code, 默认999
                    //     console.warn('登录失效code');
                    //     Message({
                    //         message: '登录失效!', // 兼容国际化词条问题
                    //         type: 'error',
                    //         duration: Settings.messageDuration,
                    //     });
                    //     // window.location.href = data.location;
                    //     const backUrl = data.location + '&backUrl=' + encodeURIComponent(`${CEUtils.locat().origin}${Settings.publicPath}?${Settings.tokenType}=${getAccessInstance()}`);
                    //     store.commit('user/setLoginUrl', backUrl);
                    //     // 从注销操作返回999 操作区别其它操作
                    //     if (response.config.url === '/bm/logout') {
                    //         set('reLoginUrl', backUrl);
                    //         router.push('/999');
                    //     } else {
                    //         router.push('/401');
                    //     }
                    //     this.manager.cancelAxios('saas', '如果出现999 则中止访问网络的其它接口');
                    // }
                }
                if (response.data.status !== 200) {
                    // 屏蔽 seo 错误提示
                    if (response.data && response.data.config && response.data.config.url !== '/mwebapi/seo/seoanalyse/keywordsRank/getCurrentFlag' || !response.data.config) {
                        Message({
                            message: response.data.msg,
                            type: 'error',
                            duration: Settings.messageDuration,
                        });
                    }
                }

                // 统计接口/时长/路由
                endTime = new Date().getTime()
                if(response){
                    store.dispatch('requestCont/addAsyncMethods',{  // 如果响应成功  记录当前请求
                        requestName: action,  // 请求路径
                        requestStatus: response.status,  // 请求代码  成功200  失败记录失败代码
                        printMsg: response.statusText,  //  请求错误信息
                        time: endTime - startTime  //  请求时间
                    });
                }

                return response.data;
            },
            error => {
                // 统计接口/时长/路由
                endTime = new Date().getTime()
                store.dispatch('requestCont/addAsyncMethods',{  // 如果响应成功  记录当前请求
                    requestName: action,  // 请求路径
                    requestStatus: error && error.response && error.response.status && error.response.status,  // 请求代码  成功200  失败记录失败代码
                    printMsg: error && error.response && error.response.statusText && error.response.statusText,  //  请求错误信息
                    time: endTime - startTime  //  请求时间
                });

                if (error.message === REPEATSYMBOL) {
                    console.log('取消重复请求');
                    return;
                } else {
                    Message({
                        message: error.message,
                        type: 'error',
                        duration: Settings.messageDuration,
                    });
                }

                return Promise.reject(error);
            },
        );
    }

    async request(options) {
        /**
         * 1、使用某一种规则包装此接口配置单
         * 2、baseUrl，url，method，dataType，authPermission，prop包含的属性
         * 3、baseUrl：由swagger提供公用字符串
         * 4、url：由swagger提供排除baseUrl剩下的字符串
         * 5、method：由swagger提供，转换统一大写
         * 6、dataType：由swagger里请求content-type决定，或其它方式
         * 7、authPermission：由swagger的http请求头，或其它方式
         * 8、prop入参规则，由async-validator规则编写
         */
        const instance = axios.create({
            // `timeout` 指定请求超时的毫秒数(0 表示无超时时间)
            // 如果请求话费了超过 `timeout` 的时间，请求将被中断
            // 默认: 5000
            timeout: '0' || Settings.requestTimeout,
        });
        // axios属性配置
        const _options = await this.setConfig(options);
        // 属性及校验配置
        const optionsAndProp = merge(options, _options);
        this.interceptors(instance, optionsAndProp);
        // 返回
        return instance(optionsAndProp);
    }
}

export function request({ ...options }) {
    // let appId = getRequest().appId || '';
    // if (appId) {
    //     options.url += '?appId=' + appId; //1600018169';
    // } else {
    //     options.url += '?appId=143150160001';
    // }

    return new BasicRequest().request(options);
}

// 对外开放request的调用，main.js增加Vue.use(Request, {……})
export function install(Vue, { ...options } = {}) {
    const create = new BasicRequest().request(options);
    Vue.prototype.$ce.request = Vue.ce.request = create;
}
